import { useState } from 'react'
import { useModal } from '@ebay/nice-modal-react'
import {
  Dropdown,
  useGlobalState,
  usePaginatedAssignmentList,
} from '@pepper/ui'

import createContent from 'assets/images/create-menu/create-content.png'
import createTopicsKeywords from 'assets/images/create-menu/create-topics-keywords.png'
import orderContentFromExperts from 'assets/images/create-menu/order-content-from-experts.png'

import CreateMenuItem from './CreateMenuItem'
import IdeaLabMenu from './IdeaLabMenu'
import AIDocMenu from './AIDocMenu'

import s from './CreateMenu.module.css'

type Props = {
  children: React.ReactNode
  project?: Project
  subMenuPlacement?: 'left' | 'right'
  hideOrderOption?: boolean
}

const CreateMenu = ({
  children,
  project,
  subMenuPlacement,
  hideOrderOption,
}: Props) => {
  const { permissions } = useGlobalState()
  const { show: showServiceDrawer } = useModal('service-selection-drawer')
  const { show: showUpsellBanner } = useModal('buy-cmp')

  const [isOpen, setIsOpen] = useState(false)

  const handleShowUpsellBanner = () => {
    showUpsellBanner()
    setIsOpen(false)
  }

  const hasIdeaLabAccess = permissions?.topics && permissions?.keywords
  const hasCMPAccess = permissions?.cmp
  const hasMarketplaceAccess = permissions?.marketplace

  // Have to use old api for assignments here because new api
  // doesn't return all the required properties
  const { records: assignments } = usePaginatedAssignmentList(
    {
      project_id: project?.id,
      favourite_creator: true,
      include_extra: true,
    },
    1,
    false,
    false,
    !!project && hasMarketplaceAccess
  )

  // We pick data from one of the existing assignments
  // which can be prefilled for new ones
  const firstAssignment = assignments[0]

  const dropdownMenu = (
    <div className={s.menu}>
      <CreateMenuItem
        imgSrc={createTopicsKeywords}
        title="Create Topics and Keywords"
        description="Ideal for SEO blogs. Discover or upload topics and create content effortlessly."
        permission={hasIdeaLabAccess}
        onClick={!hasIdeaLabAccess ? handleShowUpsellBanner : undefined}
        subMenuPlacement={subMenuPlacement}
      >
        {hasIdeaLabAccess && (
          <IdeaLabMenu
            projectId={project?.id}
            onClose={() => setIsOpen(false)}
          />
        )}
      </CreateMenuItem>
      <CreateMenuItem
        imgSrc={createContent}
        title="Create Content"
        description="Start crafting content instantly with Pepperdocs and the AI advantage!"
        permission={hasCMPAccess}
        onClick={!hasCMPAccess ? handleShowUpsellBanner : undefined}
        subMenuPlacement={subMenuPlacement}
      >
        {hasCMPAccess && (
          <AIDocMenu projectId={project?.id} onClose={() => setIsOpen(false)} />
        )}
      </CreateMenuItem>
      {hasMarketplaceAccess && !hideOrderOption && (
        <CreateMenuItem
          imgSrc={orderContentFromExperts}
          title="Order Content from Expert"
          description="Only the best! Top 3% of curated experts with high skill and domain competence."
          permission={hasMarketplaceAccess}
          onClick={() => {
            showServiceDrawer({
              projectId: project?.id,
              projectBody: {
                project_title: project?.project_title,
                document_brief_id: firstAssignment?.document_brief?.id,
                genre_id: firstAssignment?.genre.id,
                language_dialect: firstAssignment?.extra?.language_dialect,
                sub_genre_id: firstAssignment?.sub_genre?.id,
              },
            })
            setIsOpen(false)
          }}
        />
      )}
    </div>
  )

  return (
    <Dropdown
      trigger={['click']}
      placement="topLeft"
      overlay={dropdownMenu}
      visible={isOpen}
      onVisibleChange={setIsOpen}
    >
      {children}
    </Dropdown>
  )
}

export default CreateMenu
